import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { T, Var } from 'gt-react';
import { ArrowsClockwiseIcon, CaretDownIcon } from '@phosphor-icons/react';
import type { SopImprovementStatus } from '@erp/shared-types';
import { useSopImprovementList } from '@/api/rpc/hooks/agent/useSopImprovements';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { SopImprovementReview } from './SopImprovementReview';
import paths from '@/paths';

export function SopImprovementQueue({
  sopId,
  taskId,
  showHeading = true,
}: {
  sopId?: string;
  taskId?: string;
  showHeading?: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const [opened, setOpened] = useState<string | null>(null);
  const query = useSopImprovementList({ sopId, taskId, offset });
  return (
    <div className="flex flex-col gap-3">
      {taskId && (
        <div className="flex items-center justify-between gap-2 text-sm">
          <T context="SOP proposal queue filtered to a source task">
            Showing proposals from this task.
          </T>
          <Button size="sm" variant="outline" asChild>
            <Link
              to={paths.authenticated.backgroundAgents.procedures}
              search={{ review: true }}
            >
              <T context="remove task filter from SOP proposal queue">
                All Proposals
              </T>
            </Link>
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between">
        {showHeading && (
          <h2 className="font-semibold">
            <T context="shared queue of task feedback SOP improvement proposals">
              Procedure Improvement Review
            </T>
          </h2>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => void query.refetch()}
        >
          <ArrowsClockwiseIcon data-icon="inline-start" />
          <T context="refresh SOP proposal review queue">Refresh</T>
        </Button>
      </div>
      {query.isPending && (
        <p>
          <T context="loading SOP improvement proposals">Loading proposals…</T>
        </p>
      )}
      {query.error && (
        <AlertContainer variant="destructive">
          <AlertDescription>{query.error.message}</AlertDescription>
        </AlertContainer>
      )}
      {query.data?.total === 0 && (
        <p className="text-sm text-muted-foreground">
          <T context="no SOP improvement proposals in review queue">
            No proposals yet. New task feedback will appear here when it
            suggests a reusable procedure improvement.
          </T>
        </p>
      )}
      {query.data?.proposals.map((proposal) => (
        <Card key={proposal.id}>
          <CardHeader>
            <CardTitle>{proposal.sopTitle}</CardTitle>
            <CardDescription>{proposal.summary}</CardDescription>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                <ProposalStatus status={proposal.status} />
              </Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setOpened(opened === proposal.id ? null : proposal.id)
                }
                aria-expanded={opened === proposal.id}
              >
                <CaretDownIcon data-icon="inline-start" />
                <T context="open or close details of a SOP feedback proposal">
                  Review Changes
                </T>
              </Button>
            </div>
          </CardHeader>
          {opened === proposal.id && (
            <CardContent>
              <SopImprovementReview
                key={`${proposal.id}-${proposal.reviewRevision}`}
                proposal={proposal}
              />
            </CardContent>
          )}
        </Card>
      ))}
      {query.data && query.data.total > 25 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 25))}
          >
            <T context="previous page of SOP proposals">Previous</T>
          </Button>
          <span>
            <T context="SOP proposal pagination position">
              <Var>{offset + 1}</Var>–
              <Var>{Math.min(offset + 25, query.data.total)}</Var> of{' '}
              <Var>{query.data.total}</Var>
            </T>
          </span>
          <Button
            variant="outline"
            disabled={offset + 25 >= query.data.total}
            onClick={() => setOffset(offset + 25)}
          >
            <T context="next page of SOP proposals">Next</T>
          </Button>
        </div>
      )}
    </div>
  );
}

function ProposalStatus({ status }: { status: SopImprovementStatus }) {
  switch (status) {
    case 'pending':
      return <T context="SOP proposal status">Awaiting Review</T>;
    case 'accepted':
      return <T context="SOP proposal status">Accepted</T>;
    case 'dismissed':
      return <T context="SOP proposal status">Dismissed</T>;
    case 'stale':
      return <T context="SOP proposal status">Needs Regeneration</T>;
    case 'superseded':
      return <T context="SOP proposal status">Superseded</T>;
  }
}
